import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TableFooter,
} from "@material-ui/core";
import { map, not, partial } from "ramda";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Typography from "@material-ui/core/Typography";
import TablePagination from "@material-ui/core/TablePagination";
import { useJobErrors } from "./processingDiscoveryServices";
import { DateTime } from "luxon";
import Tooltip from "@material-ui/core/Tooltip";
import OpenIcon from "@material-ui/icons/Launch";
import IconButton from "@material-ui/core/IconButton";
import MetadataFileDiscoveryErrorDialog from "./MetadataFileDiscoveryErrorDialog";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import RealTimeSearchBar from "../../creator-application/common/RealTimeSearchBar";
import { useSearchFilter } from "../../common/use-searchFilter";
import ErrorTypeSelect from "./ErrorTypeSelect";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 200,
    maxWidth: 600,
  },

  countCell: {
    minWidth: 50,
    maxWidth: 60,
  },
}));

const initialSearchState = {
  orderBy: null,
  orderDirection: "asc",
  eventType: null,
  appliedFilter: null,
  page: 0,
};

const MetadataFileDiscoveryErrorsCard = ({ jobId }) => {
  const classes = useStyles();

  const [discoveryErrors, setDiscoveryErrors] = useState([]);
  const [total, setTotal] = useState(0);

  const {
    state,
    handleUpdatePage,
    handleUpdateFilter,
    handleUpdateSearch,
  } = useSearchFilter(initialSearchState);

  const errorsResponse = useJobErrors(
    jobId,
    state.eventType,
    state.appliedFilter,
    state.page,
  );
  useEffect(() => {
    if (errorsResponse.isSuccess && notBlank(errorsResponse.data)) {
      setDiscoveryErrors(errorsResponse.data.content);
      setTotal(errorsResponse.data.totalElements);
    } else {
      setDiscoveryErrors([]);
      setTotal(0);
    }
  }, [errorsResponse.isSuccess, errorsResponse.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentError, setCurrentError] = useState(null);
  const handleViewClick = useCallback(
    metadataDiscoveryError => {
      console.log("Handling view click...");
      console.log(metadataDiscoveryError);
      setCurrentError(metadataDiscoveryError);
      setDialogOpen(true);
    },
    [setCurrentError, setDialogOpen],
  );

  const handleDialogCancel = useCallback(() => {
    setDialogOpen(false);
    setCurrentError(null);
  }, [setCurrentError, setDialogOpen]);

  const [collapsed, setCollapsed] = useState(false);
  const handleCollapseClick = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Metadata File Discovery Errors"
        classes={{ root: classes.header }}
        action={
          <Tooltip title="Add Application">
            <IconButton onClick={handleCollapseClick}>
              {collapsed ? (
                <ExpandMoreIcon color="primary" fontSize="large" />
              ) : (
                <ExpandLessIcon color="primary" fontSize="large" />
              )}
            </IconButton>
          </Tooltip>
        }
      />

      {not(collapsed) && (
        <CardContent classes={{ root: classes.content }}>
          <RealTimeSearchBar
            placeHolder="Search Media Id..."
            query={state.appliedFilter}
            onQueryChange={handleUpdateFilter}
            otherFilters={
              <Box width={350} ml={4}>
                <ErrorTypeSelect
                  value={state.eventType}
                  onChange={partial(handleUpdateSearch, ["eventType"])}
                />
              </Box>
            }
          />
          {errorsResponse.isError && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              <Typography color="error">
                Error loading Metadata File Discovery Errors.
              </Typography>
            </Box>
          )}
          {errorsResponse.isLoading && <PanelLoadingIndicator />}
          {errorsResponse.isSuccess && isBlank(discoveryErrors) && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              No Errors with the Metadata File Discovery Event.
            </Box>
          )}
          {errorsResponse.isSuccess && notBlank(discoveryErrors) && (
            <TableContainer>
              <Table stickyHeader aria-label="Metadata File Discovery Errors">
                <TableHead>
                  <TableRow>
                    <TableCell
                      key="eventTimestamp"
                      align="left"
                      className={classes.contentCell}
                    >
                      Timestamp
                    </TableCell>

                    <TableCell
                      key="eventType"
                      align="left"
                      className={classes.cell}
                    >
                      Type
                    </TableCell>

                    <TableCell
                      key="mediaId"
                      align="left"
                      className={classes.cell}
                    >
                      Media Id
                    </TableCell>

                    <TableCell
                      key="taskName"
                      align="left"
                      className={classes.cell}
                    >
                      Task Name
                    </TableCell>

                    <TableCell
                      key="taskRetry"
                      align="center"
                      className={classes.countCell}
                    >
                      Task Retry Count
                    </TableCell>

                    <TableCell
                      key="actions"
                      align="center"
                      style={{ width: 100 }}
                    />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {map(
                    discoveryError => (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={
                          discoveryError.jobId +
                          DateTime.fromISO(
                            discoveryError.eventTimestamp,
                          ).toMillis()
                        }
                      >
                        <TableCell
                          key={
                            "eventTimestamp:" +
                            discoveryError.jobId +
                            DateTime.fromISO(
                              discoveryError.eventTimestamp,
                            ).toMillis()
                          }
                          align="left"
                          className={classes.contentCell}
                        >
                          {DateTime.fromISO(discoveryError.eventTimestamp)
                            .toUTC()
                            .toLocaleString(
                              DateTime.DATETIME_SHORT_WITH_SECONDS,
                            )}
                        </TableCell>

                        <TableCell
                          key={
                            "eventType:" +
                            discoveryError.jobId +
                            DateTime.fromISO(
                              discoveryError.eventTimestamp,
                            ).toMillis()
                          }
                          align="left"
                        >
                          {discoveryError.eventType}
                        </TableCell>

                        <TableCell
                          key={
                            "mediaId:" +
                            discoveryError.jobId +
                            DateTime.fromISO(
                              discoveryError.eventTimestamp,
                            ).toMillis()
                          }
                          align="left"
                          className={classes.contentTypeCell}
                        >
                          {discoveryError.mediaId}
                        </TableCell>

                        <TableCell
                          key={
                            "taskName:" +
                            discoveryError.jobId +
                            DateTime.fromISO(
                              discoveryError.eventTimestamp,
                            ).toMillis()
                          }
                          align="left"
                        >
                          {discoveryError.taskName}
                        </TableCell>

                        <TableCell
                          key={
                            "taskRetry:" +
                            discoveryError.jobId +
                            DateTime.fromISO(
                              discoveryError.eventTimestamp,
                            ).toMillis()
                          }
                          align="center"
                        >
                          {discoveryError.taskRetry}
                        </TableCell>

                        <TableCell
                          key={
                            "actions:" +
                            discoveryError.jobId +
                            DateTime.fromISO(
                              discoveryError.eventTimestamp,
                            ).toMillis()
                          }
                          align="center"
                          style={{ width: 100 }}
                        >
                          <Box display="flex" justifyContent="space-between">
                            <Tooltip title="View Error">
                              <IconButton
                                onClick={() => handleViewClick(discoveryError)}
                              >
                                <OpenIcon fontSize="large" color="primary" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ),
                    discoveryErrors,
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      classes={{
                        root: classes.pagination,
                      }}
                      rowsPerPageOptions={[10]}
                      colSpan={6}
                      count={total}
                      rowsPerPage={10}
                      page={state.page}
                      onChangePage={(event, page) => handleUpdatePage(page)}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      )}

      {dialogOpen && (
        <MetadataFileDiscoveryErrorDialog
          open={dialogOpen}
          metadataFileDiscoveryError={currentError}
          onCancel={handleDialogCancel}
        />
      )}
    </Card>
  );
};

export default MetadataFileDiscoveryErrorsCard;
